import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Camera } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { CITIES } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PhotoField = ({ id, label, hint, file, onChange }) => {
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  return (
    <div>
      <label htmlFor={id} data-testid={`${id}-label`} className="block cursor-pointer border-2 border-dashed border-zinc-300 hover:border-brand rounded-sm p-4 transition-colors duration-200">
        <span className="flex items-center gap-3">
          {previewUrl ? (
            <img src={previewUrl} alt={`Preview — ${label}`} data-testid={`${id}-preview`}
              className="w-16 h-16 object-cover rounded-sm border border-zinc-200 shrink-0" />
          ) : (
            <Camera className="w-6 h-6 text-brand shrink-0" />
          )}
          <span className="min-w-0">
            <span className="block text-sm font-bold text-brand-ink truncate">{label}</span>
            <span className="block text-xs text-zinc-500 truncate">{file ? `${file.name} — tap to change` : "Tap to add a photo"}</span>
            {file && <span className="block text-xs text-brand font-medium mt-0.5">Photo attached ✓</span>}
          </span>
        </span>
        <input id={id} data-testid={id} type="file" accept="image/*" className="sr-only"
          onChange={(e) => onChange(e.target.files?.[0] || null)} />
      </label>
      <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">{hint}</p>
    </div>
  );
};

const PHOTO_FIELDS = [
  {
    key: "outdoor_unit_photo",
    label: "1. Outside Unit — full view",
    hint: "Step back and show the unit plus the space around it. This tells us the unit size and how much working room our installers have.",
  },
  {
    key: "outdoor_nameplate_photo",
    label: "2. Outside Unit Nameplate — close up",
    hint: "A close-up of the data plate on the outdoor unit. The model and serial numbers let us match tonnage and refrigerant type exactly.",
  },
  {
    key: "indoor_unit_photo",
    label: "3. Inside Unit — full view",
    hint: "Step back and show the full indoor unit and its surroundings, including any closet or space it sits in. Clearance affects which systems fit.",
  },
  {
    key: "indoor_nameplate_photo",
    label: "4. Inside Unit Nameplate — close up",
    hint: "A close-up of the data plate on the indoor unit (air handler). This confirms your system pairing so your quote is spot on.",
  },
  {
    key: "install_area_photo",
    label: "5. Installation Area — anything else",
    hint: "Any extra photo showing access points, clearance, attic entries, or anything relevant to the install. Helps us price the job right the first time.",
  },
];

const BRANDS = ["Goodman", "Daikin", "Bryant", "Rheem", "Trane", "Carrier", "American Standard", "Ruud", "Coldflow"];

const ContactFields = ({ form, set, setForm }) => (
  <>
    <Input data-testid="estimate-name-input" required placeholder="Full name" value={form.name} onChange={set("name")} className="rounded-sm" />
    <Input data-testid="estimate-phone-input" required placeholder="Phone" type="tel" value={form.phone} onChange={set("phone")} className="rounded-sm" />
    <Input data-testid="estimate-email-input" required placeholder="Email" type="email" value={form.email} onChange={set("email")} className="rounded-sm" />
    <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
      <SelectTrigger data-testid="estimate-city-select" className="rounded-sm"><SelectValue placeholder="Your city" /></SelectTrigger>
      <SelectContent>
        {CITIES.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}
        <SelectItem value="Other">Other / Not listed</SelectItem>
      </SelectContent>
    </Select>
    <Input data-testid="estimate-sqft-input" required placeholder="Home square footage (e.g. 1800)" type="number" min="200" max="20000" value={form.square_footage} onChange={set("square_footage")} className="rounded-sm" />
    <Select value={form.floors} onValueChange={(v) => setForm({ ...form, floors: v })} required>
      <SelectTrigger data-testid="estimate-floors-select" className="rounded-sm"><SelectValue placeholder="Number of floors" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="1">1 floor</SelectItem>
        <SelectItem value="2">2 floors</SelectItem>
        <SelectItem value="3+">3+ floors</SelectItem>
      </SelectContent>
    </Select>
    <div className="sm:col-span-2">
      <Select value={form.preferred_brand} onValueChange={(v) => setForm({ ...form, preferred_brand: v })}>
        <SelectTrigger data-testid="estimate-brand-select" className="rounded-sm"><SelectValue placeholder="Preferred Brand" /></SelectTrigger>
        <SelectContent>
          {BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          <SelectItem value="No preference">No preference, please recommend the best fit for my home</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </>
);

const PhotoSection = ({ photos, setPhotos }) => (
  <div className="sm:col-span-2">
    <p className="text-sm font-bold text-brand-ink">Photos of your current system</p>
    <p className="text-xs text-zinc-500 mt-0.5 mb-3">Optional, but each photo helps us quote you faster and more accurately — no site visit needed.</p>
    <div className="space-y-4">
      {PHOTO_FIELDS.map((f) => (
        <PhotoField key={f.key} id={`estimate-${f.key.replace(/_/g, "-")}`} label={f.label} hint={f.hint}
          file={photos[f.key]} onChange={(file) => setPhotos((p) => ({ ...p, [f.key]: file }))} />
      ))}
    </div>
  </div>
);

const WarrantyCallout = () => (
  <div data-testid="estimate-warranty-callout" className="mt-6 bg-brand-subtle border-l-4 border-brand rounded-sm p-4">
    <p className="text-sm text-brand-ink leading-relaxed">
      <span className="font-heading font-bold">Every installation includes a 3-year labor warranty.</span>{" "}
      Add an annual maintenance plan at checkout and keep your system protected and running at peak performance year round.
    </p>
    <p data-testid="estimate-permit-note" className="text-xs text-zinc-600 mt-2">
      All AC replacements include permit handling as required by South Florida code.
    </p>
  </div>
);

export const EstimateForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", square_footage: "", floors: "", preferred_brand: "", message: "" });
  const [photos, setPhotos] = useState({});
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    Object.entries(photos).forEach(([k, f]) => { if (f) fd.append(k, f); });
    try {
      await api.post("/estimates", fd);
      track("estimate_form_submit", { city: form.city, preferred_brand: form.preferred_brand });
      navigate("/thank-you");
    } catch (err) {
      setStatus("idle");
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <form data-testid="estimate-form" onSubmit={submit} className="bg-white border-2 border-brand p-6 sm:p-8 rounded-sm">
      <h3 className="font-heading font-black text-2xl text-brand-ink mb-1">Get Your Free AC Replacement Estimate</h3>
      <p className="text-sm text-zinc-600 mb-6">A few quick photos and your home details — we'll call you with an honest, upfront price. No pressure, no obligation.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ContactFields form={form} set={set} setForm={setForm} />
        <PhotoSection photos={photos} setPhotos={setPhotos} />
        <Textarea data-testid="estimate-message-input" placeholder="Anything else we should know? (age of system, issues, brand preference...)" value={form.message} onChange={set("message")} className="sm:col-span-2 rounded-sm" rows={3} />
      </div>
      {error && <p data-testid="estimate-form-error" className="text-sm text-brand-red mt-3">{error}</p>}
      <WarrantyCallout />
      <button data-testid="estimate-form-submit-btn" type="submit" disabled={status === "loading"}
        className="mt-4 w-full bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold py-3.5 rounded-sm transition-colors duration-200 flex items-center justify-center gap-2">
        {status === "loading" && <Loader2 className="w-4 h-4 animate-spin" />}
        Get My Free Estimate
      </button>
    </form>
  );
};
